import { getImageAttributes } from './getImageAttributes.js';
import { Container, Sizer, SizerImage, StyledImage } from './Image.styles.js';
import { type ImageProps } from './types.js';
import { encodeBase64 } from '@contra/utilities/encodeBase64';
import { forwardRef, useMemo } from 'react';

export const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      grayscale,
      height,
      imageTransforms,
      layout = 'intrinsic',
      loading = 'lazy',
      objectFit,
      objectPosition,
      src,
      style,
      uid,
      width,
      ...imageProps
    }: ImageProps,

    ref,
  ) => {
    const imageAttributes = useMemo(() => {
      if (typeof src === 'string') {
        return {
          src,
        };
      }

      return getImageAttributes({
        grayscale,
        height,
        imageTransforms,
        uid,
        width,
      });
    }, [grayscale, height, imageTransforms, src, uid, width]);

    return (
      <Container
        $height={height}
        $width={width}
        layout={layout}
      >
        {(layout === 'intrinsic' || layout === 'responsive') &&
        height !== undefined &&
        width !== undefined ? (
          <Sizer
            $height={height}
            $width={width}
            layout={layout}
          >
            {layout === 'intrinsic' ? (
              <SizerImage
                alt=""
                aria-hidden
                role="presentation"
                src={`data:image/svg+xml;base64,${encodeBase64(
                  `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" version="1.1" />`,
                )}`}
              />
            ) : null}
          </Sizer>
        ) : null}

        <StyledImage
          ref={ref}
          {...imageProps}
          {...imageAttributes}
          className={className}
          decoding="async"
          loading={loading}
          objectFit={objectFit}
          objectPosition={objectPosition}
          style={style}
        />
      </Container>
    );
  },
);
